/**
 * Hotel Checkout API
 *
 * Handles hotel checkout/payment initiation API calls via HOTEL_REVIEW_MCP API.
 * Uses MCP format with action_type="Payment-Checkout".
 * Returns checkout URL similar to flights prebook.
 */
import type {
  HotelCheckoutMCPRequest,
  MCPCheckoutTravellerDetail,
  MCPDeviceInfo,
  MCPLocationContext,
} from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { baseFetch, createApiError, getCBContext } from '../booking-review/baseApi';
import { generateUUID } from '@/shared/utils/uuid';

// ============================================
// Error Class
// ============================================

export const HotelCheckoutError = createApiError('HotelCheckoutError');

// ============================================
// Request Types
// ============================================

export interface HotelCheckoutRequestParams {
  sessionId: string;
  userId: string;
  /** Transaction key from booking review */
  transactionKey: string;
  /** Array of traveller details */
  travellerDetails: MCPCheckoutTravellerDetail[];
  /** Hotel name */
  hotelName: string;
  /** Hotel ID */
  hotelId: string;
  /** City code (e.g., CTGOI for Goa) */
  cityCode?: string | undefined;
  /** Currency code (optional, defaults to INR) */
  currency?: string | undefined;
}

// Re-export types from contracts for convenience
export type { MCPCheckoutTravellerDetail } from '@contracts';

// ============================================
// Response Types
// ============================================

export interface HotelCheckoutData {
  checkoutUrl?: string;
  checkoutId?: string;
  bookingId?: string;
  correlationKey?: string;
  transactionKey?: string;
  paymentRespMessage?: string;
  currency?: string;
  totalAmount?: string;
}

export interface HotelCheckoutResource {
  data: HotelCheckoutData;
}

export interface HotelCheckoutDataWrapper {
  resources: HotelCheckoutResource[];
}

export interface HotelCheckoutErrorDetail {
  error_code: string;
  error_message: string;
  error_desc?: string;
  service_name?: string;
}

export interface HotelCheckoutResponse {
  status: string;
  data?: HotelCheckoutDataWrapper;
  errors?: HotelCheckoutErrorDetail[];
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  status?: string;
  data?: {
    resources?: Array<{ data?: HotelCheckoutData }>;
  };
  errors?: HotelCheckoutErrorDetail[];
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build device info for MCP request
 */
function buildDeviceInfo(): MCPDeviceInfo {
  const { deviceId } = getCBContext();
  return {
    device_id: deviceId,
    app_ver: '1.21.1',
    device_type: 'DESKTOP',
    platform: 'DESKTOP',
  };
}

/**
 * Build location context for MCP request
 */
function buildLocationContext(
  hotelName: string,
  hotelId: string,
  cityCode?: string,
): MCPLocationContext {
  return {
    hotel: hotelName,
    hotel_id: hotelId,
    city_code: cityCode,
  };
}

/**
 * Build the MCP payload for checkout request
 */
export function buildMCPPayload(params: HotelCheckoutRequestParams): HotelCheckoutMCPRequest {
  const { authToken, currency } = getCBContext();

  return {
    request_id: generateUUID(),
    conversation_id: params.sessionId,
    uuid: params.userId,
    auth: authToken,
    device_info: buildDeviceInfo(),
    rephrased_query: 'Process payment checkout',
    intents: ['Review'],
    action_type: 'Payment-Checkout',
    location_context: buildLocationContext(params.hotelName, params.hotelId, params.cityCode),
    transactionKey: params.transactionKey,
    travellerDetails: params.travellerDetails,
    currency: params.currency ?? currency ?? 'INR',
  };
}

// ============================================
// API Function
// ============================================

/**
 * Initiate hotel checkout
 * POST /hotels/checkout?sessionId=...&userId=...
 *
 * Sends MCP payload with structure:
 * {
 *   request_id, conversation_id, uuid, auth, device_info,
 *   rephrased_query, intents, action_type: "Payment-Checkout",
 *   location_context: { hotel, hotel_id },
 *   transactionKey, travellerDetails, currency
 * }
 */
export async function fetchHotelCheckout(
  params: HotelCheckoutRequestParams,
): Promise<HotelCheckoutResponse> {
  const payload = buildMCPPayload(params);

  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.CHECKOUT,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'HotelCheckoutClient',
  });

  // Network/HTTP error
  if (error) {
    return {
      status: 'RESPONSE_STATUS_ERROR',
      errors: [{ error_code: error.code, error_message: error.message }],
    };
  }

  // Check for errors in response
  if (data?.errors && data.errors.length > 0) {
    logger.warn('[HotelCheckoutClient] API returned errors:', data.errors);
    return {
      status: data.status ?? 'RESPONSE_STATUS_ERROR',
      errors: data.errors,
    };
  }

  // Success
  const checkoutUrl = data?.data?.resources?.[0]?.data?.checkoutUrl;
  logger.warn('[HotelCheckoutClient] Success:', { hasCheckoutUrl: Boolean(checkoutUrl) });

  const response: HotelCheckoutResponse = {
    status: data?.status ?? 'RESPONSE_STATUS_SUCCESS',
  };

  if (data?.data) {
    response.data = data.data as HotelCheckoutDataWrapper;
  }

  return response;
}
